import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, FileText } from "lucide-react";
import { useTran } from "@/hooks";
import { useKpiTargetsStore } from "../hooks";

const TargetSummaryTable = observer(() => {
  const { t } = useTran();
  const {
    activeCategory,
    allTargets,
    getSelectedQuantitativeDisclosuresByCategory,
    hasSelectedMetrics,
    selectedFramework,
    selectedYear,
    targetSearchTerm,
  } = useKpiTargetsStore();
  const searchTerm = targetSearchTerm.trim().toLowerCase();
  const disclosures = getSelectedQuantitativeDisclosuresByCategory(
    selectedFramework,
    activeCategory,
  );

  if (!hasSelectedMetrics) {
    return (
      <div className="rounded-radius bg-muted/40 p-6 text-center">
        <Circle className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <p className="mt-3 text-sm text-muted-foreground">
          {t("No disclosures selected for this target setting.")}
        </p>
      </div>
    );
  }

  const enrichedTargets = useMemo(() => {
    const targetsByCode = new Map(
      allTargets
        .filter((target) => target.year === selectedYear)
        .map((target) => [target.disclosureCode, target]),
    );

    const rows = disclosures.map((disclosure) => {
      const codeKey =
        disclosure.masterMetricId ||
        `${disclosure.framework}_${disclosure.code}`.toUpperCase();
      const target = targetsByCode.get(codeKey);
      return {
        code: disclosure.code,
        disclosureCode: codeKey,
        disclosureTitle: disclosure.title,
        framework: disclosure.framework,
        category: disclosure.category,
        year: selectedYear,
        annual: target?.annual ?? null,
        quarterly: target?.quarterly ?? null,
        monthly: target?.monthly ?? null,
        unit: target?.unit || disclosure.unit || "",
      };
    });

    if (!searchTerm) return rows;

    return rows.filter((row) => {
      const haystack = [
        row.disclosureCode,
        row.disclosureTitle,
        row.framework,
        row.category,
        row.annual != null ? `${row.annual}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [disclosures, searchTerm, selectedYear, allTargets]);

  if (enrichedTargets.length === 0) {
    return (
      <div className="rounded-radius bg-muted/40 p-6 text-center">
        <Circle className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <p className="mt-3 text-sm text-muted-foreground">
          {t("No targets set yet.")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 bg-card p-4 rounded-radius">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">
          {t("Target Summary")}
        </h3>
        <Badge className="bg-primary text-primary-foreground">
          {enrichedTargets.length}{" "}
          {enrichedTargets.length === 1 ? t("Target") : t("Targets")} {t("Set")}
        </Badge>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/60 text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3 text-left">{t("Disclosure")}</th>
              <th className="px-4 py-3 text-left">{t("Framework")}</th>
              <th className="px-4 py-3 text-left">{t("Category")}</th>
              <th className="px-4 py-3 text-center">{t("Year")}</th>
              <th className="px-4 py-3 text-center">{t("Annual Target")}</th>
              <th className="px-4 py-3 text-center">{t("Status")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {enrichedTargets.map((target) => {
              const hasPeriodicTargets =
                (target.quarterly && target.quarterly.some((val) => val > 0)) ||
                (target.monthly && target.monthly.some((val) => val > 0));
              const hasAnnualTarget =
                target.annual !== null && target.annual !== undefined;

              return (
                <tr
                  key={target.disclosureCode}
                  className="transition-colors hover:bg-muted/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {target.code}
                        </p>
                        <p className="font-medium text-foreground">
                          {target.disclosureTitle}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="text-xs">
                      {target.framework}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="secondary" className="text-xs">
                      {target.category}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-medium text-foreground">
                      {target.year}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {hasAnnualTarget ? (
                      <>
                        <span className="font-semibold text-foreground">
                          {Number(target.annual).toLocaleString()}
                        </span>
                        {target.unit ? (
                          <span className="ml-1 text-xs text-muted-foreground">
                            {target.unit}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-xs font-semibold text-muted-foreground">
                        {t("Not set")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {hasAnnualTarget && hasPeriodicTargets ? (
                      <div className="flex items-center justify-center gap-1 text-emerald-400">
                        <CheckCircle2 className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {t("Complete")}
                        </span>
                      </div>
                    ) : hasAnnualTarget ? (
                      <div className="flex items-center justify-center gap-1 text-amber-400">
                        <Circle className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {t("Annual Only")}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1 text-muted-foreground">
                        <Circle className="h-4 w-4" />
                        <span className="text-xs font-medium">
                          {t("Not set")}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-radius bg-muted/40 p-3">
        <div className="grid grid-cols-3 gap-4 text-center text-sm">
          <div>
            <p className="text-2xl font-bold text-primary">
              {enrichedTargets.length}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("Total Targets")}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-secondary">
              {
                enrichedTargets.filter(
                  (target) =>
                    (target.quarterly &&
                      target.quarterly.some((val) => val > 0)) ||
                    (target.monthly && target.monthly.some((val) => val > 0)),
                ).length
              }
            </p>
            <p className="text-xs text-muted-foreground">
              {t("With Periodic Targets")}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-400">
              {selectedYear}
            </p>
            <p className="text-xs text-muted-foreground">{t("Target Year")}</p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TargetSummaryTable;
